import { useState } from 'react';
import { Play } from 'lucide-react';
import { Button, Chip, Dialog } from '@nyce/admin-kit/ui';
import { EmptyState, ErrorBox, TableScroller, useAsync } from '../../../components/common';
import { formatDateTime } from '../../../lib/format';
import { ConfirmDialog } from '../../../pages/databases/shared';
import { monitorsFor, type MonitorActionResult, type MonitorAlert } from '../../api/monitors';
import { alertFinding } from './form';
import { RowsTable, SnapshotView, ToneChip } from './parts';

/** One alert: the finding, its proposed actions (executable ones behind a confirm), the action log, the row snapshot. */
export function AlertDialog({
  target,
  alert,
  monitorName,
  canWrite,
  onClose,
}: {
  target: string;
  alert: MonitorAlert;
  monitorName?: string;
  canWrite: boolean;
  onClose: () => void;
}) {
  const api = monitorsFor(target);
  const f = alertFinding(alert);
  const log = useAsync(() => api.actionLog(alert.id), [target, alert.id]);
  const [results, setResults] = useState<Record<number, MonitorActionResult>>({});
  const [confirm, setConfirm] = useState<number | null>(null);
  const execute = async (index: number) => {
    const r = await api.executeAction(alert.id, index);
    setResults((cur) => ({ ...cur, [index]: r }));
    setConfirm(null);
    void log.reload();
  };
  return (
    <Dialog
      open
      onClose={onClose}
      title={`Alert ${alert.entityKey}${monitorName ? ` · ${monitorName}` : ''}`}
      maxWidth={860}
    >
      <p className="nyk-inline" data-testid="alert-detail">
        <ToneChip kind="alert" value={alert.status} />
        <span className="nyk-ui-hint">
          {alert.systemId} · opened {formatDateTime(alert.openedAt)} · last seen{' '}
          {formatDateTime(alert.lastSeenAt)}
          {alert.resolvedAt ? ` · resolved ${formatDateTime(alert.resolvedAt)}` : ''}
        </span>
      </p>
      {f.title && (
        <h3 className="bm-section-title nyk-inline">
          {f.title} <ToneChip kind="severity" value={f.severity} />
        </h3>
      )}
      {f.finding ? (
        <p className="bm-finding">{f.finding}</p>
      ) : (
        <p className="nyk-ui-hint">No finding yet: the investigation has not run for this alert.</p>
      )}
      {f.content?.map((c, i) =>
        c.type === 'table' && c.columns ? (
          <div key={i}>
            {c.name && <div className="nyk-ui-eyebrow">{c.name}</div>}
            <RowsTable rows={c.data ?? []} columns={c.columns} />
          </div>
        ) : (
          <p key={i} className="bm-finding">
            {c.text}
          </p>
        )
      )}

      <h3 className="bm-section-title">Proposed actions</h3>
      {!f.actions.length && <p className="nyk-ui-hint">None proposed.</p>}
      {f.actions.length > 0 && (
        <ul className="bm-actions-list" data-testid="alert-actions">
          {f.actions.map((a, i) => {
            const r = results[i];
            return (
              <li key={i} data-action={i}>
                <Chip size="sm">{a.type.replace('_', ' ')}</Chip>
                <span className="bm-action-text">{a.description}</span>
                {a.type === 'email_report' ? (
                  canWrite && (
                    <Button size="sm" icon={<Play size={14} />} onClick={() => setConfirm(i)}>
                      Execute
                    </Button>
                  )
                ) : (
                  <span className="nyk-ui-hint">manual: done by a person</span>
                )}
                {r && (
                  <div className={r.success ? 'bm-ok' : 'bm-error'} data-testid="action-result">
                    {r.success
                      ? `Done${r.detail ? `: ${r.detail}` : ''}`
                      : `Failed: ${r.error ?? 'unknown error'}`}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <h3 className="bm-section-title">Action log</h3>
      <ErrorBox error={log.error} />
      {!log.loading && !log.data?.length && <EmptyState title="Nothing executed yet." />}
      {!!log.data?.length && (
        <TableScroller>
          <table className="nyk-ui-table nyk-cards" data-testid="action-log">
            <thead>
              <tr>
                <th>When</th>
                <th>Action</th>
                <th>By</th>
                <th>Result</th>
              </tr>
            </thead>
            <tbody>
              {log.data.map((e) => (
                <tr key={e.id} data-log={e.id}>
                  <td className="nyk-card-title">{formatDateTime(e.executedAt)}</td>
                  <td data-label="Action">
                    <Chip size="sm">{e.actionType.replace('_', ' ')}</Chip> {e.description ?? ''}
                  </td>
                  <td data-label="By">{e.executedBy}</td>
                  <td data-label="Result">
                    <Chip size="sm" className={e.success ? 'nyk-tone-green' : 'nyk-tone-red'}>
                      {e.success ? 'ok' : 'failed'}
                    </Chip>{' '}
                    {e.result ?? ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroller>
      )}

      <h3 className="bm-section-title">Row snapshot</h3>
      <SnapshotView value={alert.rowSnapshot} />

      {confirm !== null && f.actions[confirm] && (
        <ConfirmDialog
          title="Execute this action?"
          confirmLabel="Execute action"
          onClose={() => setConfirm(null)}
          onConfirm={() => execute(confirm)}
        >
          <p>{f.actions[confirm].description}</p>
          <p className="nyk-ui-hint">
            nyce.buddy carries it out now (an email report goes out to the monitor’s recipients) and logs it
            on this alert. It cannot be undone.
          </p>
        </ConfirmDialog>
      )}
    </Dialog>
  );
}
